export const getOptionValue = (opt) => {
  if (opt == null) return "";
  if (typeof opt === "string" || typeof opt === "number" || typeof opt === "boolean") {
    return String(opt);
  }
  if (typeof opt === "object") {
    return String(opt.name ?? opt.value ?? opt.id ?? opt.label ?? "");
  }
  return String(opt);
};

export const getOptionLabel = (opt) => {
  if (opt == null) return "";
  if (typeof opt === "string" || typeof opt === "number" || typeof opt === "boolean") {
    return String(opt);
  }
  if (typeof opt === "object") {
    return String(opt.label ?? opt.name ?? opt.value ?? opt.id ?? "");
  }
  return String(opt);
};

export const getEffectiveSelectorOptions = ({ dependsOn, parentValue, options }) => {
  if (!dependsOn?.options_map) return options;

  const parentNull = dependsOn?.null_label;
  const key = !parentValue || parentValue === parentNull ? "all" : String(parentValue);
  const list = dependsOn.options_map[key];
  return Array.isArray(list) ? list : [];
};

export const buildSelectorOptions = ({ isMulti, allowNull, nullLabel, effectiveOptions }) => {
  if (!isMulti && allowNull && nullLabel) {
    return [
      nullLabel,
      ...effectiveOptions.filter((option) => getOptionValue(option) !== nullLabel),
    ];
  }
  return effectiveOptions;
};

export const isSelectorDisabledByDependency = ({ dependsOn, parentValue }) => {
  return (
    !!dependsOn &&
    (!parentValue || (dependsOn.null_label && parentValue === dependsOn.null_label))
  );
};

export const normalizeSelectorValue = ({
  isMulti,
  value,
  fullOptions,
  allowNull,
  nullLabel,
  disabledByDependency,
}) => {
  if (isMulti) {
    const current = Array.isArray(value) ? value : [];
    const next = current.filter(
      (item) =>
        fullOptions.some((option) => getOptionValue(option) === item) ||
        (allowNull && item === nullLabel)
    );
    const normalized = allowNull && next.includes(nullLabel) ? [nullLabel] : next;
    return arraysEqual(normalized, current) ? undefined : normalized;
  }

  const hasValue = value != null && value !== "";
  const valueExists = fullOptions.some(
    (option) => getOptionValue(option) === value || getOptionLabel(option) === value
  );

  if (hasValue && !valueExists) {
    return allowNull ? nullLabel : "";
  }

  if (!hasValue && !disabledByDependency && fullOptions.length > 0) {
    const firstValue = getOptionValue(fullOptions[0]);
    return allowNull ? nullLabel : firstValue;
  }

  return undefined;
};

export const getSelectedMultiItems = ({ value, allowNull, nullLabel, fullOptions }) => {
  const raw = Array.isArray(value) ? value : [];
  if (allowNull && raw.includes(nullLabel)) return [nullLabel];
  return fullOptions.filter((option) => raw.includes(getOptionValue(option)));
};

export const getSelectedSingleItem = ({ isMulti, value, allowNull, nullLabel, fullOptions }) => {
  if (isMulti) return null;
  if (value == null || value === "") return allowNull ? nullLabel : null;
  const found = fullOptions.find(
    (option) => getOptionValue(option) === value || getOptionLabel(option) === value
  );
  return found ?? (allowNull ? nullLabel : null);
};

export const getMultiChangeValue = ({ selectedItems, allowNull, nullLabel }) => {
  const items = Array.isArray(selectedItems) ? selectedItems : [];
  if (
    allowNull &&
    items.some((item) => item === nullLabel || getOptionValue(item) === nullLabel)
  ) {
    return [nullLabel];
  }
  return items.map((item) => getOptionValue(item)).filter(Boolean);
};

export const getSingleChangeValue = ({ selectedItem, nullLabel }) => {
  return selectedItem === nullLabel ? nullLabel : getOptionValue(selectedItem);
};

const arraysEqual = (left, right) => {
  return (
    Array.isArray(left) &&
    Array.isArray(right) &&
    left.length === right.length &&
    left.every((item, index) => item === right[index])
  );
};
